import { useState } from 'react'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { useMultilingualProjects, useDeleteMultilingualProject, MultilingualProjectItem } from '@/hooks/useMultilingualProjects'
import { MultilingualProjectEditor } from '@/components/admin/MultilingualProjectEditor'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu'
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { MoreHorizontal, Edit, Trash2, Eye, Star, Plus } from 'lucide-react'
import { toast } from 'sonner'

export function ProjectManager() {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language as 'zh' | 'en'
  const [deleteProjectId, setDeleteProjectId] = useState<string | null>(null)
  const [editingProject, setEditingProject] = useState<MultilingualProjectItem | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  
  const { data: projects, isLoading, refetch } = useMultilingualProjects(undefined, currentLanguage)
  const deleteProject = useDeleteMultilingualProject()

  const handleDelete = async () => {
    if (!deleteProjectId) return
    
    try {
      await deleteProject.mutateAsync(deleteProjectId)
      toast.success(t('admin.projects.deleteSuccess'))
      setDeleteProjectId(null)
    } catch (error) {
      toast.error(t('admin.projects.deleteError'))
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
      case 'planned':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
    }
  }

  // Show editor when creating or editing
  if (isCreating || editingProject) {
    return (
      <MultilingualProjectEditor
        project={editingProject}
        onClose={() => {
          setEditingProject(null)
          setIsCreating(false)
        }}
        onSuccess={() => refetch()}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-3 bg-muted rounded w-full mb-2"></div>
              <div className="h-3 bg-muted rounded w-2/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">
          {projects?.length || 0} Projects
        </h2>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Multilingual Project
        </Button>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {projects?.map((project) => (
          <Card key={project.id} className="relative group hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {project.title}
                    {project.is_featured && (
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                    )}
                  </CardTitle>
                  {project.subtitle && (
                    <CardDescription className="mt-1">
                      {project.subtitle}
                    </CardDescription>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => window.open(`/projects/${project.id}`, '_blank')}>
                      <Eye className="h-4 w-4 mr-2" />
                      {t('admin.actions.view')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setEditingProject(project)}>
                      <Edit className="h-4 w-4 mr-2" />
                      {t('admin.actions.edit')}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={() => setDeleteProjectId(project.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('admin.actions.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {project.description}
              </p>
              
              <div className="flex items-center justify-between">
                <Badge className={getStatusColor(project.status)}>
                  {t(`project.status.${project.status}`)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {project.category_id}
                </span>
              </div>

              {project.tags && project.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {project.tags.slice(0, 3).map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {project.tags.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{project.tags.length - 3}
                    </Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!deleteProjectId} onOpenChange={() => setDeleteProjectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.projects.deleteConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.projects.deleteConfirm.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}